import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RefreshCw, Trash2, ShieldAlert, CheckCircle2, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const providerColor = { plaid: "#4C956C", mono: "#E07A5F" };

function StatusPill({ status, error }) {
  if (error) {
    return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FCE7E9] text-[#B4283A]">
      <XCircle className="w-3 h-3" /> {status || "error"}
    </span>;
  }
  const ok = typeof status === "number" && status < 400;
  return <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${ok ? "bg-[#EAF4EE] text-[#2F6B4A]" : "bg-[var(--hover)] text-[var(--muted-text)]"}`}>
    {ok && <CheckCircle2 className="w-3 h-3" />} {status ?? "—"}
  </span>;
}

export default function Diagnostics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [targetEmail, setTargetEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const isAdmin = user?.role === "admin";

  const load = async (email) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/diagnostics/banks", {
        params: email ? { user_email: email } : {},
      });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load diagnostics");
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const clear = async () => {
    try {
      await api.delete("/admin/diagnostics/banks", {
        params: targetEmail ? { user_email: targetEmail } : {},
      });
      toast.success("Diagnostics cleared");
      load(targetEmail || undefined);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear");
    }
  };

  if (!isAdmin) {
    return (
      <AppShell>
        <div className="card-flat p-8 text-center max-w-md mx-auto">
          <ShieldAlert className="w-8 h-8 mx-auto text-[#B4283A]" />
          <h1 className="font-display text-2xl mt-3">Admin only</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">
            This page is only visible to the account with role=admin.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Admin</div>
          <h1 className="font-display text-4xl mt-2">Bank diagnostics</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1 max-w-2xl">
            Last {data?.max_entries || 30} raw Plaid & Mono API calls per user, with request &amp; response bodies. Use this to self-diagnose bank-connect failures without touching code.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">User email (optional)</Label>
            <Input
              data-testid="diag-user-email"
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              placeholder={user.email}
              className="h-9 mt-1 w-64"
            />
          </div>
          <Button
            data-testid="diag-refresh-btn"
            variant="outline"
            onClick={() => load(targetEmail || undefined)}
            disabled={loading}
            className="h-9 gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            data-testid="diag-clear-btn"
            variant="outline"
            onClick={clear}
            className="h-9 gap-2"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Clear
          </Button>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--divider)] flex items-center justify-between">
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
            {data ? `Log for ${data.user_email} · ${data.entries.length} entries` : "Loading…"}
          </div>
          {data && data.entries.length === 0 && (
            <span className="text-xs text-[var(--muted-text)]">Nothing recorded yet — trigger a bank connect / sync to populate.</span>
          )}
        </div>
        {data?.entries?.length ? (
          <div data-testid="diag-entries" className="divide-y divide-[var(--divider)]">
            {data.entries.map((e, idx) => {
              const key = `${idx}-${e.at}`;
              const isOpen = !!expanded[key];
              return (
                <div key={key} className="px-5 py-3">
                  <button
                    data-testid={`diag-row-${idx}`}
                    onClick={() => setExpanded({ ...expanded, [key]: !isOpen })}
                    className="w-full flex items-center gap-3 text-left"
                  >
                    <span
                      className="w-1.5 h-8 rounded-full"
                      style={{ backgroundColor: providerColor[e.provider] || "#5C5C5C" }}
                    />
                    <span className="text-xs uppercase tracking-wider font-medium w-16">{e.provider}</span>
                    <span className="text-xs text-[var(--muted-text)] w-16">{e.method}</span>
                    <span className="text-sm truncate flex-1 font-mono">{e.path}</span>
                    <StatusPill status={e.status} error={e.error} />
                    <span className="text-xs text-[var(--muted-text)] w-32 text-right">
                      {formatDistanceToNow(new Date(e.at), { addSuffix: true })}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">Request</div>
                        <pre className="text-xs bg-[var(--hover)] p-3 rounded max-h-72 overflow-auto whitespace-pre-wrap break-all">{typeof e.request === "string" ? e.request : JSON.stringify(e.request, null, 2)}</pre>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">Response</div>
                        <pre className="text-xs bg-[var(--hover)] p-3 rounded max-h-72 overflow-auto whitespace-pre-wrap break-all">{typeof e.response === "string" ? e.response : JSON.stringify(e.response, null, 2)}</pre>
                      </div>
                      {e.error && (
                        <div className="md:col-span-2">
                          <div className="text-[10px] uppercase tracking-widest text-[#B4283A] mb-1">Error</div>
                          <pre className="text-xs bg-[#FCE7E9] text-[#B4283A] p-3 rounded whitespace-pre-wrap break-all">{e.error}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          !loading && data && (
            <div className="p-10 text-center text-sm text-[var(--muted-text)]">
              No entries yet. Try connecting Plaid or Mono — every request will be captured here for you to inspect.
            </div>
          )
        )}
      </div>
    </AppShell>
  );
}
